'use client';
// CE QUI A CHANGÉ ENTRE DEUX VERSIONS (RQ, 22/10/2026) — pour savoir ce qu'il y a
// de nouveau SANS relire le document en entier. La comparaison est calculée par
// le serveur, sur le texte, sans IA (`backend/src/comparaison-texte.ts`) : elle est
// exacte et complète. Quand elle ne peut pas être faite (archive manquante,
// classeur…), on le DIT — jamais un « aucun changement » qui serait faux.
import React, { useEffect, useState } from 'react';
import { card, Skeleton, EmptyState } from '@/components/ui';

interface Segment { t: '=' | '-' | '+'; s: string }
interface Changement {
  nature: 'ajoute' | 'supprime' | 'modifie';
  section: string;
  tableau: boolean;
  avant?: string;
  apres?: string;
  segments?: Segment[];
}
interface Reponse {
  comparable: boolean;
  motif?: string;
  de: string;
  a: string;
  bilan?: string;
  identique?: boolean;
  changements?: Changement[];
  stats?: { ajoutes: number; supprimes: number; modifies: number; inchanges: number };
  tronque?: boolean;
}

const ETIQUETTE: Record<Changement['nature'], string> = { ajoute: 'Ajouté', supprime: 'Supprimé', modifie: 'Modifié' };
const COULEUR: Record<Changement['nature'], { fond: string; texte: string }> = {
  ajoute: { fond: 'var(--green-50)', texte: 'var(--green-600)' },
  supprime: { fond: 'var(--red-50)', texte: 'var(--red-600)' },
  modifie: { fond: 'var(--amber-50)', texte: 'var(--amber-600)' },
};

function Texte({ c }: { c: Changement }) {
  if (c.nature === 'modifie' && c.segments) {
    return (
      <span style={{ lineHeight: 1.6 }}>
        {c.segments.map((s, i) => s.t === '=' ? <span key={i}>{s.s}</span>
          : s.t === '-' ? <del key={i} style={{ background: 'var(--red-50)', color: 'var(--red-600)', textDecoration: 'line-through', borderRadius: 'var(--radius-sm)' }}>{s.s}</del>
            : <ins key={i} style={{ background: 'var(--green-50)', color: 'var(--green-600)', textDecoration: 'none', fontWeight: 600, borderRadius: 'var(--radius-sm)' }}>{s.s}</ins>)}
      </span>
    );
  }
  if (c.nature === 'supprime') return <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', lineHeight: 1.6 }}>{c.avant}</span>;
  return <span style={{ lineHeight: 1.6 }}>{c.apres}</span>;
}

/**
 * `de` / `a` : les versions à comparer. Sans elles, le serveur compare la plus récente
 * à celle qui la précède. `vide` : ce qu'on écrit quand il n'y a rien à comparer
 * (première version) — par défaut, le composant ne montre rien dans ce cas.
 */
export function ChangementsVersion({ documentId, de, a, masquerSiPremiere = false }: {
  documentId: string; de?: string; a?: string; masquerSiPremiere?: boolean;
}) {
  const [rep, setRep] = useState<Reponse | null>(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let vivant = true;
    setRep(null); setErreur('');
    const q = new URLSearchParams();
    if (de) q.set('de', de);
    if (a) q.set('a', a);
    void fetch(`/api/ged/ged/docs/${documentId}/comparaison${q.toString() ? `?${q}` : ''}`, { credentials: 'include' })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!vivant) return;
        if (!r.ok) setErreur(j?.error?.message || 'La comparaison n’a pas pu être calculée.');
        else setRep(j as Reponse);
      })
      .catch(() => { if (vivant) setErreur('La comparaison n’a pas pu être calculée.'); });
    return () => { vivant = false; };
  }, [documentId, de, a]);

  if (rep && !rep.comparable && masquerSiPremiere && !rep.de) return null;

  const titre = rep ? `Ce qui change dans la v${rep.a}${rep.de ? ` par rapport à la v${rep.de}` : ''}` : 'Ce qui change';
  const groupes: { section: string; items: Changement[] }[] = [];
  for (const c of rep?.changements || []) {
    const dernier = groupes[groupes.length - 1];
    if (dernier && dernier.section === c.section) dernier.items.push(c);
    else groupes.push({ section: c.section, items: [c] });
  }

  return (
    <div style={{ ...card, overflow: 'hidden' }}>
      <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>{titre}</h3>
        {rep?.comparable && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{rep.bilan}</span>}
      </div>
      {!rep && !erreur && <Skeleton rows={3} height={36} />}
      {erreur && <EmptyState titre={erreur} />}
      {rep && !rep.comparable && <EmptyState titre="Comparaison impossible" indice={rep.motif} />}
      {rep?.comparable && rep.identique && (
        <EmptyState titre="Le texte est identique" indice="Aucun passage ajouté, supprimé ou modifié. La mise en forme n’est pas comparée." />
      )}
      {rep?.comparable && !rep.identique && (
        <div>
          {groupes.map((g, gi) => (
            <div key={`${g.section}-${gi}`} style={{ borderTop: gi ? '1px solid var(--border-subtle)' : 'none' }}>
              {g.section && <div style={{ padding: '10px 20px 2px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>{g.section}</div>}
              {g.items.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '9px 20px', alignItems: 'flex-start' }}>
                  <span style={{ flex: 'none', minWidth: 68, textAlign: 'center', fontSize: 11.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: COULEUR[c.nature].fond, color: COULEUR[c.nature].texte }}>
                    {ETIQUETTE[c.nature]}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text-body)', overflowWrap: 'anywhere' }}>
                    {c.tableau && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Tableau · </span>}
                    <Texte c={c} />
                  </span>
                </div>
              ))}
            </div>
          ))}
          {rep.tronque && <div style={{ padding: '10px 20px', fontSize: 12.5, color: 'var(--text-muted)', borderTop: '1px solid var(--border-subtle)' }}>La liste est coupée : le document comporte plus de changements que cet écran n’en affiche.</div>}
          <div style={{ padding: '8px 20px 12px', fontSize: 12, color: 'var(--text-muted)', borderTop: '1px solid var(--border-subtle)' }}>
            Comparaison du texte (paragraphes et lignes de tableau), calculée à partir des fichiers : la mise en forme n’est pas comparée.
          </div>
        </div>
      )}
    </div>
  );
}
